import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Home, ChevronRight } from 'lucide-react-native';
import { router } from 'expo-router';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { Skeleton, TextLink } from '@components/common';
import { getReadinessLevelFromPercent, getReadinessColor } from '../../utils/readiness';
import type { Structure } from '../../types';

interface ClaimedPropertiesCarouselProps {
  claimedStructureIds: string[];
  claimedStructures: Structure[];
  isLoadingStructures: boolean;
}

function ReadinessChip({ percent }: { percent: number }) {
  const { colors } = useTheme();
  const level = getReadinessLevelFromPercent(percent);
  const color = getReadinessColor(level, colors) ?? colors.textSecondary;
  return (
    <Text style={[styles.readiness, { color }]}>
      {Math.round(percent)}
      <Text style={styles.readinessUnit}>%</Text>
    </Text>
  );
}

export function ClaimedPropertiesCarousel({
  claimedStructureIds,
  claimedStructures,
  isLoadingStructures,
}: ClaimedPropertiesCarouselProps) {
  const { colors } = useTheme();
  // Two fetches back this list: the claim ids, then the structure docs. The
  // skeleton only ever covered the first, so between them the list rendered
  // its container with nothing in it — a bare rule under the section label.
  // Treat "ids arrived, docs haven't" as loading too.
  const loading =
    (isLoadingStructures && claimedStructureIds.length === 0) ||
    (claimedStructureIds.length > 0 && claimedStructures.length === 0);

  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: colors.textSecondary }]}>My properties</Text>
        {claimedStructureIds.length > 0 && (
          <TextLink
            label="See all"
            onPress={() => router.push('/profile/my-properties')}
            textStyle={styles.seeAll}
            accessibilityLabel="See all properties"
          />
        )}
      </View>

      <View style={[styles.list, { borderTopColor: colors.border }]}>
        {loading
          ? [0, 1].map((i) => (
              <View
                key={`skeleton-${i}`}
                style={[styles.row, { borderBottomColor: colors.border }]}>
                <Skeleton width={20} height={20} borderRadius={BORDER_RADIUS.sm} />
                <View style={styles.rowBody}>
                  <Skeleton width="70%" height={14} />
                  <Skeleton width="40%" height={12} style={{ marginTop: 6 }} />
                </View>
              </View>
            ))
          : claimedStructures.map((s) => {
              const pct = s.claimSummary?.progressPercent;
              return (
                <TouchableOpacity
                  key={s.id}
                  onPress={() => router.push(`/structure/${s.id}`)}
                  accessibilityRole="button"
                  accessibilityLabel={`${s.address ?? 'Property'}, view details`}
                  style={[styles.row, { borderBottomColor: colors.border }]}>
                  <Home size={20} color={colors.textSecondary} />
                  <View style={styles.rowBody}>
                    <Text style={[styles.address, { color: colors.text }]} numberOfLines={1}>
                      {s.address || 'Unknown address'}
                    </Text>
                    {s.city ? (
                      <Text
                        style={[styles.city, { color: colors.textSecondary }]}
                        numberOfLines={1}>
                        {s.city}
                      </Text>
                    ) : null}
                  </View>
                  {pct != null ? <ReadinessChip percent={pct} /> : null}
                  <ChevronRight size={18} color={colors.textTertiary} />
                </TouchableOpacity>
              );
            })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    marginBottom: SPACING.lg,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.sm,
  },
  sectionTitle: {
    ...TYPOGRAPHY.overline,
  },
  seeAll: {
    ...TYPOGRAPHY.label,
  },
  // Hairline-divided rows, not a bordered card (mockup `.rowlink`): a rule
  // above the first row, a rule under each.
  list: {
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowBody: {
    flex: 1,
  },
  address: {
    ...withWeight(TYPOGRAPHY.body, 'medium'),
  },
  city: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 1,
  },
  readiness: {
    ...TYPOGRAPHY.button,
    fontVariant: ['tabular-nums'],
  },
  readinessUnit: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'semibold'),
  },
});
